import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User } from '../../../../models/User';
import { Heading, Subheading } from '../../../../components/Heading';
import { Card } from '../../../../components/Card';
import { Input } from '../../../../components/Input';
import { Textarea } from '../../../../components/Textarea';
import { Select } from '../../../../components/Select';
import { Button } from '../../../../components/Button';
import showLoadingToast from '../../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import { useRoles } from '../../../../hooks/useRoles';
import Loading from '../../../../components/Loading';
import { useStockIn } from '../../../../hooks/useStockIn';
import { useSetupUsergroup } from '../../../../hooks/useSetupUsergroup';
import useUserService from '../../../../hooks/useUserService';
import { UserContext } from '../../../../context/userContext';
import { useWarehouses } from '../../../../hooks/useWarehouse';
import { StockIn } from '../../../../models/StockIn';

// Utility function to format date to yyyy-MM-dd based on local time
const formatDate = (isoString: string): string => {
    const date = new Date(isoString);

    // Get the year, month, and day using local time
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based
    const day = date.getDate().toString().padStart(2, '0'); // Ensure two digits for day

    return `${year}-${month}-${day}`; // Format as yyyy-MM-dd
};

const EditStockInPage = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [stockIn, setStockIn] = useState<StockIn | null>(null);
    const { fetchStockInById, loading, error, updateStockIn } = useStockIn();
    const { suppliers, logistics } = useSetupUsergroup();
    const { users } = useUserService();
    const { currentUser } = useContext(UserContext);
    const { warehouses } = useWarehouses();
    const filteredWarehouses = warehouses.filter(warehouse => warehouse.hq !== '1');

    useEffect(() => {
        document.title = "Edit Stock In";
    }, []);

    useEffect(() => {
        const fetchedStockInDetails = async () => {
            try {
                const fetchedStockIn = await fetchStockInById(Number(id));
                if (fetchedStockIn!.date) {
                    fetchedStockIn!.date = formatDate(fetchedStockIn!.date);
                }
                setStockIn(fetchedStockIn);
            } catch (err) {
                showErrorToast(`Error fetching stock in ${id}: ${err}`);
            }
        }
        fetchedStockInDetails();
    }, [id]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setStockIn(prev => prev ? ({ ...prev, [name]: value }) : null);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!stockIn) {
            return;
        }

        if (!stockIn.date || !stockIn.logistic_id || !stockIn.receiver_id || !stockIn.reference_no || !stockIn.supplier_id || !stockIn.supplier_invoice || !stockIn.warehouse_id || !stockIn.created_by) {
            showErrorToast("Please fill in all field before submit");
            return;
        }

        try {
            const res = await updateStockIn(Number(id), stockIn!);
            showLoadingToast(`Updating ${stockIn!.reference_no} ...`, 100);
            if (res.dbsuccess) {
                setTimeout(() => {
                    navigate('/gp/stocks/stockin');
                    showSuccessToast(`${stockIn!.reference_no} updated successfully`);
                }, 1000);
            } else {
                showErrorToast(`Failed updating stock-in data`);
            }
        } catch (err) {
            showErrorToast(`Failed updating stock-in data: ${err}`);
        }
    };


    if (loading) return <p>Loading...</p>;
    if (error) return <p>{error}</p>;
    if (!stockIn) return <p>Stock in data not found.</p>;

    return (
        <form method="post" onSubmit={handleSubmit} className="mx-auto max-w-6xl">
            <div className="lg:flex lg:items-center lg:justify-between mb-5">
                <Heading>Edit Stock In</Heading>
                <div className='mt-5 flex lg:ml-4 lg:mt-0'>
                    <Button type="reset" outline onClick={() => navigate(-1)}>
                        Cancel
                    </Button>
                    <Button type="submit" className='ml-3' disabled={(!stockIn.date || !stockIn.logistic_id || !stockIn.receiver_id || !stockIn.reference_no || !stockIn.supplier_id || !stockIn.supplier_invoice || !stockIn.warehouse_id || !stockIn.created_by)}>Save</Button>
                </div>
            </div>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Stock In Information</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            Information on incoming inventory to the warehouse.
                        </p>
                    </div>

                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Stock-in Date</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        type='date'
                                        aria-label="Date"
                                        name="date"
                                        value={stockIn.date!}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Warehouse</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Select
                                        aria-label="Warehouse"
                                        name="warehouse_id"
                                        value={stockIn.warehouse_id}
                                        onChange={handleChange}
                                    >
                                        {filteredWarehouses.map(warehouse => (
                                            <option key={warehouse.id.toString()} value={warehouse.id.toString()}>
                                                {warehouse.warehouse_name}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Reference No</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Reference No"
                                        name="reference_no"
                                        value={stockIn.reference_no!}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Supplier</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Select
                                        aria-label="Supplier"
                                        name="supplier_id"
                                        value={stockIn.supplier_id!.toString()}
                                        onChange={handleChange}
                                    >
                                        {suppliers.map(supplier => (
                                            <option key={supplier.id} value={supplier.id!.toString()}>
                                                {supplier.fullname}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Supplier Invoice</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Supplier Invoice"
                                        name="supplier_invoice"
                                        value={stockIn.supplier_invoice!}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Logistic</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Select
                                        aria-label="Logistic"
                                        name="logistic_id"
                                        value={stockIn.logistic_id!.toString()}
                                        onChange={handleChange}
                                    >
                                        {logistics.map(logistic => (
                                            <option key={logistic.id} value={logistic.id!.toString()}>
                                                {logistic.fullname}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Received by</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Select
                                        aria-label="Receiver Id"
                                        name="receiver_id"
                                        value={stockIn.receiver_id!.toString()}
                                        onChange={handleChange}
                                    >
                                        {users.map(user => (
                                            <option key={user.id} value={user.id!.toString()}>
                                                {user.fullname}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </Card>
            <div className="flex justify-end gap-4 my-6">
                <Button type="reset" plain onClick={() => navigate(-1)}>
                    Cancel
                </Button>
                <Button type="submit" disabled={(!stockIn.date || !stockIn.logistic_id || !stockIn.receiver_id || !stockIn.reference_no || !stockIn.supplier_id || !stockIn.supplier_invoice || !stockIn.warehouse_id || !stockIn.created_by)}>Save</Button>
            </div>
        </form>
    );
};

export default EditStockInPage;
